import type { FieldPresentationBlock } from '#layers/data-model/constants/fields';
import type { AnyResolvedField } from '#layers/data-model/types/schema/resolved-schema';
import { getRelationFieldDisplayDefaults } from '#layers/data-model/constants/fields';
import fieldTypes from '#layers/data-model/field-types';
import { isPrimitiveTypeTemplate } from '#layers/data-model/field-types/field-type';
import { resolveDefaultDisplayBlock } from '#layers/data-model/utils/fields/default-display';
import { resolveFieldPresentationBlock } from '#layers/data-model/utils/fields/presentation';
import { isSingleRelationField } from '#layers/data-model/utils/schema/collection';

export interface FieldDisplayBlockOptions {
	block: string;
	props: Record<string, unknown>;
}

/**
 * Resolve the display block ID and user-configured props for a field.
 *
 * Field-level `displayId` is treated as the selected display block. Default
 * display props only apply when that selected block is the default block for the field.
 * Field-level `displayOptions` are passed through as props, along with `fieldId`.
 */
export function getFieldDisplayBlockOptions(field: AnyResolvedField): FieldDisplayBlockOptions {
	const display = resolveFieldPresentationBlock(getDefaultDisplayBlock(field), field.meta?.displayId, {
		fieldId: field.id,
		...field.meta?.displayOptions,
	});

	return {
		block: display.block,
		props: display.props ?? {},
	};
}

/**
 * Default display block used when a field has no explicit `displayId`.
 *
 * For primitive fields this follows input, field-type, then primitive-type recommendations. This is
 * shared with the appearance editor so its preview matches what the field renders.
 */
function getDefaultDisplayBlock(field: AnyResolvedField): FieldPresentationBlock {
	if (isSingleRelationField(field)) {
		return getRelationFieldDisplayDefaults(field);
	}

	const fieldType = field.meta?.typeTemplateId ? fieldTypes[field.meta.typeTemplateId] : undefined;
	const primitiveFieldType = fieldType && isPrimitiveTypeTemplate(fieldType) ? fieldType : undefined;

	return resolveDefaultDisplayBlock({
		fieldType: primitiveFieldType,
		context: {
			primitiveType: field.type,
			isNewField: false,
			collectionId: field.collectionId,
		},
		inputId: field.meta?.inputId,
		typeOptions: field.meta?.typeOptions,
	});
}
